<template>
  <div>
    <div :style="style" class="relative">
      <button
        ref="button"
        class="block w-full h-10 p-2 pr-8 text-left placeholder-gray-400 bg-no-repeat border border-gray-300 rounded appearance-none dropdown-bg-color truncate"
        :class="{ 'bg-gray-50': disabled }"
        type="button"
        @[isClick]="toggle"
        :aria-expanded="isOpen"
        :aria-haspopup="true"
        :aria-controls="dropdownId"
        :aria-label="`${placeholder} dropdown`"
        :disabled="disabled"
        :aria-disabled="disabled"
      >
        <span v-if="value !== null">{{ value.label }}</span>
        <span v-else>{{ placeholder }}</span>
      </button>

      <div
        v-show="isOpen"
        class="absolute left-0 right-0 bottom-auto mt-1 overflow-auto dropdown-bg-color border rounded shadow max-h-dropdown z-10"
        :id="dropdownId"
        role="listbox"
        :aria-label="`${placeholder} options`"
      >
        <div
          v-if="filteredOptions.length > 3 || search !== ''"
          class="sticky top-0 p-2 border-b dropdown-bg-color"
        >
          <input
            ref="search"
            type="text"
            class="block w-full h-10 p-2 placeholder-gray-400 border border-gray-300 rounded appearance-none dropdown-bg-color"
            v-model="search"
            @keydown.esc="close"
            role="searchbox"
            :aria-label="`Search ${placeholder} options`"
            :aria-controls="listId"
            :placeholder="`Search ${placeholder} options`"
          />
        </div>
        <ul
          v-show="filteredOptions.length > 0"
          :id="listId"
          role="listbox"
          :aria-label="`${placeholder} options list`"
        >
          <li
            v-for="(option, index) in filteredOptions"
            :key="index"
            role="option"
            :aria-selected="value === option"
          >
            <span
              class="block p-2 text-sm cursor-pointer hover:selected-item"
              :class="{ 'selected-item': value === option }"
              @click="select(option)"
              :tabindex="0"
              @keydown.enter="select(option)"
              @keydown.space.prevent="select(option)"
            >{{ option.label }}</span>
          </li>
        </ul>
        <div
          class="p-2 text-sm"
          v-if="options.length > 0 && filteredOptions.length === 0"
          role="status"
          aria-live="polite"
        >
          No results found for "{{ search }}"
        </div>
        <div
          class="p-2 text-sm"
          v-if="options.length === 0"
          role="status"
          aria-live="polite"
        >
          No available options
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    options: {
      type: Array,
      default: () => [],
    },
    value: Object,
    controls: Object,
    placeholder: {
      type: String,
      default: "Choose your insurance",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      search: "",
      isOpen: false,
      dropdownId: `dropdown-${Math.random().toString(36).substr(2, 9)}`,
      listId: `list-${Math.random().toString(36).substr(2, 9)}`
    };
  },
  computed: {
    filteredOptions() {
      return this.options.filter((el) =>
        el?.label?.toLowerCase().includes(this.search.toLowerCase())
      );
    },
    isClick() {
      return this.disabled ? null : "click";
    },
    style() {
      return {
        "--dropdown-bg-color":
          this.$store.state.config?.colors?.containerBackground || "#FFFFFF",
        "--selected-bg-color":
          this.$store.state.config?.colors?.secondaryOptionColor || "#D4F3BB",
      };
    },
  },
  methods: {
    toggle() {
      this.isOpen = !this.isOpen;

      if (this.isOpen && this.filteredOptions.length > 3) {
        this.$nextTick(() => {
          this.$refs.search.focus();
        });
      }
    },
    open() {
      if (this.isOpen) {
        return;
      }
      this.isOpen = true;

      if (this.filteredOptions.length > 3) {
        this.$nextTick(() => {
          this.$refs.search.focus();
        });
      }
    },
    close() {
      if (!this.isOpen) {
        return;
      }
      this.isOpen = false;
    },
    select(option) {
      this.$emit("input", option);
      this.search = "";
      this.close();
    },
  },
};
</script>

<style scoped>
.max-h-dropdown {
  max-height: 250px;
}
.opacity-75 {
  opacity: 0.75;
}
.dropdown-bg-color {
  background-color: var(--dropdown-bg-color);
}
.selected-item {
  background-color: var(--selected-bg-color);
}
.hover\:selected-item:hover {
  background-color: var(--selected-bg-color);
}
button {
  background-image: url("data:image/svg+xml,%3Csvg fill='none' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 14 14'%3E%3Cpath d='M2.454 5.303L.636 7.121 7 13.485l6.364-6.364-1.818-1.818L7 9.849 2.454 5.303z' fill='%23191919'/%3E%3C/svg%3E");
  background-size: 14px;
  background-position-y: center;
  background-position-x: calc(100% - 6px);
}
</style>
